import { useEffect } from "react";
import { useTranslation } from "@flight-tools/i18n";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Label,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@flight-tools/ui";
import {
  Users,
  Lightning,
  Armchair,
  Baby,
  Plus,
  Minus,
  Info,
} from "@phosphor-icons/react";

import en from "./locales/en.json";
import es from "./locales/es.json";
import sv from "./locales/sv.json";

interface PassengerConfigProps {
  businessPassengers: number;
  economyPassengers: number;
  lapInfants: number;
  onBusinessChange: (value: number) => void;
  onEconomyChange: (value: number) => void;
  onLapInfantsChange: (value: number) => void;
}

interface NumberInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  label: string;
  tooltip: string;
  icon: React.ReactNode;
  badge?: React.ReactNode;
  unit: string;
}

function NumberInput({
  value,
  onChange,
  min = 0,
  max = 200,
  label,
  tooltip,
  icon,
  badge,
  unit,
}: NumberInputProps) {
  const handleDecrement = () => {
    if (value > min) {
      onChange(value - 1);
    }
  };

  const handleIncrement = () => {
    if (value < max) {
      onChange(value + 1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Allow empty input (will be treated as 0 on blur)
    if (inputValue === "") {
      onChange(0);
      return;
    }

    // Parse and validate
    const newValue = parseInt(inputValue, 10);
    if (!isNaN(newValue)) {
      const clampedValue = Math.max(min, Math.min(newValue, max));
      onChange(clampedValue);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <Label className="text-base font-medium">{label}</Label>
          {badge}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Info className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <span className="text-sm text-muted-foreground">{unit}</span>
      </div>

      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          size="icon-lg"
          onClick={handleDecrement}
          disabled={value <= min}
          aria-label="Decrease"
        >
          <Minus weight="bold" className="h-5 w-5" />
        </Button>

        <div className="relative flex-1">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={value}
            onChange={handleInputChange}
            className="h-16 w-full rounded-lg border border-input bg-card text-center text-3xl font-mono font-bold text-foreground ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label={label}
          />
        </div>

        <Button
          variant="outline"
          size="icon-lg"
          onClick={handleIncrement}
          disabled={value >= max}
          aria-label="Increase"
        >
          <Plus weight="bold" className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
}

export function PassengerConfig({
  businessPassengers,
  economyPassengers,
  lapInfants,
  onBusinessChange,
  onEconomyChange,
  onLapInfantsChange,
}: PassengerConfigProps) {
  const { t, i18n } = useTranslation("passenger-config");

  // Load colocated translations
  useEffect(() => {
    i18n.addResourceBundle("en", "passenger-config", en, true, true);
    i18n.addResourceBundle("es", "passenger-config", es, true, true);
    i18n.addResourceBundle("sv", "passenger-config", sv, true, true);
  }, [i18n]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users weight="duotone" className="h-6 w-6 text-primary" />
          {t("title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <NumberInput
          value={businessPassengers}
          onChange={onBusinessChange}
          max={999}
          label={t("businessClass")}
          tooltip={t("businessClassTooltip")}
          icon={<Lightning weight="fill" className="h-5 w-5 text-accent" />}
          badge={
            <span className="inline-flex items-center rounded-full bg-accent/20 px-2 py-0.5 text-xs font-semibold text-accent">
              Premium
            </span>
          }
          unit={t("passengers")}
        />

        <NumberInput
          value={economyPassengers}
          onChange={onEconomyChange}
          max={1000}
          label={t("economyClass")}
          tooltip={t("economyClassTooltip")}
          icon={<Armchair weight="fill" className="h-5 w-5 text-primary" />}
          unit={t("passengers")}
        />

        <NumberInput
          value={lapInfants}
          onChange={onLapInfantsChange}
          max={50}
          label={t("lapInfants")}
          tooltip={t("lapInfantsTooltip")}
          icon={<Baby weight="fill" className="h-5 w-5 text-pink-400" />}
          unit={t("infants")}
        />
      </CardContent>
    </Card>
  );
}
